import React, { useRef, useEffect, useState } from "react"
import { motion, useScroll, useTransform, useMotionValue, animate } from "framer-motion"
import CMSRender from "aldoo-ra/CMS/cms-render"
import Typography from "aldoo-ra/Typography"
import parse from "html-react-parser"

// Modified sequence timing constants for faster transitions
const SEQUENCE = {
  DESKTOP: {
    PAGE_ENTER: 0.05, // Reduced from 0.1
    FEATURES: 0.4,    // Reduced from 0.8
    PAGE_EXIT: 0.05,  // Reduced from 0.1
  },
  MOBILE: {
    PAGE_ENTER: 0.02, // Reduced from 0.05
    FEATURES: 0.1,    // Reduced from 0.2
    PAGE_EXIT: 0.02,  // Reduced from 0.05
  },
}

const createTimeline = (pages) => {
  // Safe window check - using correct breakpoint
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768
  const timing = isMobile ? SEQUENCE.MOBILE : SEQUENCE.DESKTOP
  let timelinePoints = []
  let progressPoint = 0

  pages?.forEach((page, pageIndex) => {
    const isFirstPage = pageIndex === 0

    timelinePoints.push({
      type: "pageEnter",
      id: `${page._id}-enter`,
      pageIndex,
      start: progressPoint,
      end: progressPoint + timing.PAGE_ENTER,
      initiallyVisible: isFirstPage,
    })

    progressPoint += timing.PAGE_ENTER

    const featureDuration =
      timing.FEATURES / (isMobile ? 1 : (page.features?.length || 1))
    page.features?.forEach((feature, featureIndex) => {
      timelinePoints.push({
        type: "feature",
        id: `${page._id}-feature-${featureIndex}`,
        pageIndex,
        featureIndex,
        feature,
        start: progressPoint,
        end: progressPoint + featureDuration,
        initiallyVisible: isFirstPage,
      })

      progressPoint += featureDuration
    })

    timelinePoints.push({
      type: "pageExit",
      id: `${page._id}-exit`,
      pageIndex,
      start: progressPoint,
      end: progressPoint + timing.PAGE_EXIT,
    })

    progressPoint += timing.PAGE_EXIT
  })

  const totalDuration = progressPoint
  return timelinePoints.map((point) => ({
    ...point,
    start: point.start / totalDuration,
    end: point.end / totalDuration,
  }))
}

// Feature component using motion values that update via useEffect
const Feature = ({ feature, scrollProgress, timelineData }) => {
  const opacity = useMotionValue(0)
  const scale = useMotionValue(0.9)
  const y = useMotionValue(20)

  useEffect(() => {
    const unsubscribe = scrollProgress.onChange((latest) => {
      if (!timelineData) return

      // Calculate progress within feature timeline
      const progress = Math.max(0, Math.min(1,
        (latest - timelineData.start) / (timelineData.end - timelineData.start)
      ))

      const isMobile = typeof window !== "undefined" && window.innerWidth < 768

      // Update motion values based on progress
      if (isMobile) {
        opacity.set(progress <= 0 ? 0 : progress >= 0.9 ? 0 : progress <= 0.1 ? progress * 10 : 1)
      } else {
        opacity.set(progress <= 0 ? 0 : progress >= 0.7 ? 1 : progress <= 0.3 ? progress / 0.3 : 1)
      }

      scale.set(progress <= 0.3 ? 0.9 + progress / 0.3 * 0.1 : 1)
      y.set(progress <= 0.3 ? 20 - progress / 0.3 * 20 : 0)
    })

    return () => unsubscribe()
  }, [scrollProgress, timelineData, opacity, scale, y])

  if (!feature) return null

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768

  const highlightX = parseFloat(
    feature.focus_position?.highlight_x || feature.focus_position?.x || 50
  )
  const highlightY = parseFloat(
    feature.focus_position?.highlight_y || feature.focus_position?.y || 50
  )
  const textX = parseFloat(feature.focus_position?.x || 50)
  const textY = parseFloat(feature.focus_position?.y || 50)
  const highlightRadius = parseFloat(feature.focus_position?.highlight_r || 50)
  const textWidth = isMobile ? 280 : 200

  const isTextOnLeft = textX < highlightX - textWidth / 100
  const intermediateX = textX
  const isTextAbove = textY < highlightY
  const intermediateY = isTextAbove ? textY : highlightY
  const labelX = isTextOnLeft ? 75 : 0
  const labelY = isTextAbove ? 100 : 0

  const calculateCircleIntersection = () => {
    const radiusInPercent = highlightRadius / 6.4
    const dx = intermediateX - highlightX
    const dy = intermediateY - highlightY
    const distance = Math.sqrt(dx * dx + dy * dy)
    const ratio = radiusInPercent / distance
    return {
      intersectionX: highlightX + dx * ratio,
      intersectionY: highlightY + dy * ratio,
    }
  }

  const { intersectionX, intersectionY } = calculateCircleIntersection()

  return (
    <motion.div
      className="absolute inset-0"
      style={{
        opacity,
        scale,
        y,
        transformOrigin: "center center"
      }}
      initial={{ opacity: 0 }}
    >
      {!isMobile && (
        <div className="absolute inset-0">
          <svg
            width="100%"
            height="100%"
            className="absolute inset-0"
            style={{ overflow: "visible" }}
          >
            <line
              x1={`${textX}%`}
              y1={`${textY}%`}
              x2={`${intermediateX}%`}
              y2={`${highlightY}%`}
              stroke="#2E6FE4"
              strokeWidth="1"
            />
            <line
              x1={`${intermediateX}%`}
              y1={`${highlightY}%`}
              x2={`${intersectionX}%`}
              y2={`${highlightY}%`}
              stroke="#2E6FE4"
              strokeWidth="1"
            />
            <circle cx={`${textX}%`} cy={`${textY}%`} r="3" fill="#2E6FE4" />
          </svg>
        </div>
      )}

      <motion.div
        className={`absolute ${
          isMobile
            ? "-top-[90px] left-1/2 transform -translate-x-1/2 text-center"
            : ""
        }`}
        style={
          isMobile
            ? { zIndex: 10 }
            : {
                left: `${textX}%`,
                top: `${textY}%`,
                transform: `translate(-${labelX}%, -${labelY}%)`,
                zIndex: 10,
              }
        }
      >
        <Typography variant="body1" className={`w-[${textWidth}px]`}>
          {parse(feature.title)}
        </Typography>
      </motion.div>

      <motion.div
        className="absolute bg-blue-500/15 border-[1px] border-blue-500 rounded-full -translate-x-1/2 -translate-y-1/2"
        style={{
          left: `${highlightX}%`,
          top: `${highlightY}%`,
          width: `${highlightRadius}px`,
          height: `${highlightRadius}px`,
          zIndex: 10,
        }}
      />
    </motion.div>
  )
}

// ShowcasePage component using motion values that update via useEffect
const ShowcasePage = ({
  page,
  scrollProgress,
  pageEnterData,
  pageExitData,
  featuresData,
  isFirstPage,
  isLastPage,
  sectionTitle,
}) => {
  const opacity = useMotionValue(isFirstPage ? 1 : 0)
  const scale = useMotionValue(isFirstPage ? 1 : 0.95)
  const yOffset = useMotionValue(isFirstPage ? 0 : 50)
  const exitOpacity = useMotionValue(1)
  const exitYOffset = useMotionValue(0)

  useEffect(() => {
    const unsubscribe = scrollProgress.onChange((latest) => {
      // Page enter animations
      if (pageEnterData && !isFirstPage) {
        const enterProgress = Math.max(0, Math.min(1,
          (latest - pageEnterData.start) / (pageEnterData.end - pageEnterData.start)
        ))

        opacity.set(enterProgress <= 0.3 ? 0 : enterProgress <= 0.6 ? (enterProgress - 0.3) / 0.3 : 1)
        scale.set(enterProgress <= 0.3 ? 0.95 + enterProgress / 0.3 * 0.05 : 1)
        yOffset.set(enterProgress <= 0.6 ? 50 - enterProgress / 0.6 * 50 : 0)
      }

      // Page exit animations
      if (pageExitData && !isLastPage) {
        const exitProgress = Math.max(0, Math.min(1,
          (latest - pageExitData.start) / (pageExitData.end - pageExitData.start)
        ))

        exitOpacity.set(exitProgress <= 0.3 ? 1 - exitProgress / 0.3 : 0)
        exitYOffset.set(exitProgress <= 0.6 ? -50 * exitProgress / 0.6 : -50)
      }
    })

    return () => unsubscribe()
  }, [scrollProgress, pageEnterData, pageExitData, isFirstPage, isLastPage, opacity, scale, yOffset, exitOpacity, exitYOffset])

  return (
    <motion.div
      className="absolute inset-0 w-full flex items-center justify-center"
      style={{ opacity: pageExitData && !isLastPage ? exitOpacity : opacity }}
    >
      <motion.div
        className="relative w-full flex flex-col items-center"
        style={{
          opacity,
          scale,
          y: pageExitData && !isLastPage ? exitYOffset : yOffset,
        }}
      >
        {/* Section title inside the mockup container - visible only on md+ screens */}
        {sectionTitle && (
          <div className="hidden md:block mb-20 text-center w-full">
            <Typography
              component="h2"
              variant="h1"
              className="text-center text-text max-w-[700px] lg:max-w-full mx-auto"
            >
              {parse(sectionTitle)}
            </Typography>
          </div>
        )}

        {/* Add max-height constraint to ensure phone mockup fits within viewport */}
        <div className="relative w-[210px] md:w-[250px] lg:w-[280px] mt-[64px] md:mt-0 max-h-[calc(100vh-120px)] flex items-center justify-center mx-auto">
          <img
            src={page.image}
            alt="Translator showcase"
            className="w-full h-auto max-h-[calc(100vh-120px)] object-contain"
          />
          {page.features?.map((feature, index) => (
            <Feature
              key={`${page._id}-feature-${index}`}
              feature={feature}
              scrollProgress={scrollProgress}
              timelineData={featuresData[index]}
            />
          ))}
        </div>
      </motion.div>
    </motion.div>
  )
}

const PageDots = ({ totalPages, currentPage, scrollYProgress }) => {
  const [isScrolling, setIsScrolling] = useState(false)
  const scrollTimeout = useRef(null)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true)
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current)
      }
      scrollTimeout.current = setTimeout(() => {
        setIsScrolling(false)
      }, 1000)
    }

    const unsubscribe = scrollYProgress.onChange(handleScroll)
    return () => {
      unsubscribe()
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current)
      }
    }
  }, [scrollYProgress])

  const shouldShow = useTransform(
    scrollYProgress,
    [0, 0.05, 0.95, 1],
    [0, 1, 1, 0]
  )
  const opacity = useTransform(shouldShow, (value) => (isScrolling ? value : 0))

  return (
    <motion.div
      className="fixed left-[10%] top-1/2 -translate-y-1/2 flex flex-col gap-3"
      style={{ opacity }}
      initial={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {Array.from({ length: totalPages }).map((_, index) => (
        <motion.div
          key={index}
          className={`w-3 h-3 text-blue_tt rounded-full transition-colors duration-200 ${
            index === currentPage ? "bg-blue_tt" : "bg-gray_200"
          }`}
        />
      ))}
    </motion.div>
  )
}

// Modified the calculateSequences function to reduce total scroll distance
const ShowcaseContent = ({ data, scrollYProgress }) => {
  const timeline = createTimeline(data.pages)
  const [currentPage, setCurrentPage] = useState(0)
  const contentRef = useRef(null)
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768

  useEffect(() => {
    const onChange = (latest) => {
      const currentPoint = timeline.find(
        (point) => latest >= point.start && latest <= point.end
      )

      if (currentPoint) {
        if (isMobile) {
          setCurrentPage(currentPoint.pageIndex)
        } else {
          const pageSize = 1 / data.pages?.length || 1
          const currentPageIndex = Math.min(
            Math.floor(latest / pageSize),
            (data.pages?.length || 1) - 1
          )
          setCurrentPage(currentPageIndex)
        }
      }
    }

    const unsubscribe = scrollYProgress.onChange(onChange)
    return () => unsubscribe()
  }, [scrollYProgress, data.pages?.length, timeline, isMobile])

  // Build timeline data for each page without using hooks
  const getTimelineData = (pageIndex, type, featureIndex = null) => {
    if (type === "feature") {
      return timeline.find(
        (p) =>
          p.pageIndex === pageIndex &&
          p.type === "feature" &&
          p.featureIndex === featureIndex
      )
    }
    return timeline.find(
      (p) => p.pageIndex === pageIndex && p.type === type
    )
  }

  // Calculate scroll height based on sequences - reduced multiplier for faster scrolling
  const calculateSequences = () => {
    if (isMobile) {
      // Reduce the multiplier for faster scrolling on mobile
      return data.pages?.reduce(
        (total, page) => total + (page.features?.length || 0) + 0.8, // Reduced from + 1
        0
      ) || 0
    }
    // Reduce the multiplier for faster scrolling on desktop
    return data.pages?.reduce(
      (total, page) => total + (1.5 + (page.features?.length || 0) * 0.8), // Reduced from 2 + features.length
      0
    ) || 0
  }

  const totalSequences = calculateSequences()
  const scrollHeight = `${totalSequences * 100}vh`

  return (
    <div style={{ height: scrollHeight }} ref={contentRef}>
      <PageDots
        totalPages={data.pages?.length || 0}
        currentPage={currentPage}
        scrollYProgress={scrollYProgress}
      />

      {/* Mobile title - only visible on small screens */}
      <motion.div className="pt-[57px] md:hidden">
        <Typography
          component="h2"
          variant="h1"
          className="text-center text-text max-w-[700px] lg:max-w-full mt-5 mx-auto"
        >
          {data.title && typeof data.title === "string"
            ? parse(data.title)
            : data.title}
        </Typography>
      </motion.div>

      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">
        <div className="w-full h-full md:max-w-[740px] lg:max-w-w2_l xl:max-w-w2_xl mx-auto px-4 flex items-center justify-center">
          <div className="relative w-full h-full flex items-center justify-center">
            {data.pages?.map((page, pageIndex) => (
              <ShowcasePage
                key={page._id}
                page={page}
                scrollProgress={scrollYProgress}
                pageEnterData={getTimelineData(pageIndex, "pageEnter")}
                pageExitData={getTimelineData(pageIndex, "pageExit")}
                featuresData={
                  page.features?.map((_, featureIndex) =>
                    getTimelineData(pageIndex, "feature", featureIndex)
                  ) || []
                }
                isFirstPage={pageIndex === 0}
                isLastPage={pageIndex === (data.pages?.length || 1) - 1}
                sectionTitle={pageIndex === 0 ? data.title : null} // Only pass title to first page
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

const TranslatorShowcase = () => {
  const containerRef = useRef(null)

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  })

  return (
    <div ref={containerRef} className="relative">
      <CMSRender
        contentType="T&T Showcase"
        renderItem={(data) => (
          <ShowcaseContent data={data} scrollYProgress={scrollYProgress} />
        )}
      />
    </div>
  )
}

export default TranslatorShowcase